import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { AssistantThreadRead } from '../../types/api';
import {
  createAssistantThread,
  deleteAssistantThread,
  fetchAssistantThreads,
  renameAssistantThread,
} from './assistantApi';

export interface AssistantThreadsResult {
  /** Newest activity first, as the list endpoint returns them. */
  threads: AssistantThreadRead[];
  isLoading: boolean;
  /** Load error only. The writes throw instead, so a form keeps its input. */
  error: string | null;
  refresh: () => void;
  create: () => Promise<AssistantThreadRead>;
  rename: (threadId: string, title: string) => Promise<AssistantThreadRead>;
  remove: (threadId: string) => Promise<void>;
}

interface LoadedThreads {
  businessId: string;
  threads: AssistantThreadRead[];
  error: string | null;
}

const NO_THREADS: never[] = [];

/** A thread's name in a list: its title, or what it is until it has one. */
export function threadLabel(thread: Pick<AssistantThreadRead, 'title'>): string {
  return thread.title?.trim() || 'New chat';
}

/**
 * The caller's assistant threads for one brand. Tagged by business and derived
 * during render, like `useCollection`; writes patch the list in place and throw
 * on failure. The server owns the order, so a send calls `refresh()` rather than
 * re-sorting here.
 */
export function useAssistantThreads(businessId: string | null): AssistantThreadsResult {
  const [loaded, setLoaded] = useState<LoadedThreads | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!businessId) return;
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const threads = await fetchAssistantThreads(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, threads, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded((current) => ({
          businessId,
          // A failed re-read keeps the list already on screen.
          threads: current?.businessId === businessId ? current.threads : [],
          error: getErrorMessage(caught, 'Could not load your chats.'),
        }));
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const patch = useCallback(
    (mutate: (threads: AssistantThreadRead[]) => AssistantThreadRead[]) => {
      setLoaded((current) =>
        current === null || current.businessId !== businessId
          ? current
          : { ...current, threads: mutate(current.threads) },
      );
    },
    [businessId],
  );

  const create = useCallback(async (): Promise<AssistantThreadRead> => {
    if (!businessId) throw new Error('No brand is active yet.');
    const created = await createAssistantThread(businessId);
    patch((threads) => [created, ...threads.filter((thread) => thread.id !== created.id)]);
    return created;
  }, [businessId, patch]);

  const rename = useCallback(
    async (threadId: string, title: string): Promise<AssistantThreadRead> => {
      if (!businessId) throw new Error('No brand is active yet.');
      const updated = await renameAssistantThread(businessId, threadId, title);
      patch((threads) => threads.map((thread) => (thread.id === threadId ? updated : thread)));
      return updated;
    },
    [businessId, patch],
  );

  const remove = useCallback(
    async (threadId: string): Promise<void> => {
      if (!businessId) throw new Error('No brand is active yet.');
      await deleteAssistantThread(businessId, threadId);
      patch((threads) => threads.filter((thread) => thread.id !== threadId));
    },
    [businessId, patch],
  );

  const isCurrent = loaded !== null && loaded.businessId === businessId;

  return {
    threads: isCurrent ? loaded.threads : NO_THREADS,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    refresh,
    create,
    rename,
    remove,
  };
}
